// Coffee cup page (spec 2026-10-06-coffee-cup-design). The DOCUMENT (src/doc.js) is the state: every control
// builds a NEW doc through sanitize(), and the worker (geometry.worker.js via worker-client.js) builds the
// meshes 120 ms after the last change. No geometry runs here. Same shape as apps/tray/src/main.js.
import '@3dp/ui/shell.css';
import '@3dp/ui/picker.css';   // the font control and its sheet (unify-ui ship 4a): before the tool's own stylesheet
import '@3dp/ui/colours.css';  // the colour control (unify-ui ship 4b): before the tool's own stylesheet too
import './main.css';
import { publishBarHeight, publishHeaderHeight } from '@3dp/ui/bar-height';
import { pageLanguage } from '@3dp/ui/locales';
import { asset } from '@3dp/ui/asset';
import { S } from './strings.js';
import { DEFAULT_DOC, LIMITS, PRESETS, LOOKS, FITS, MODES, SOURCES, STYLE_KEYS, sanitize, sizeKey, fmt } from './doc.js';
import { createWorkerClient } from './worker-client.js';
import { createPreview } from './preview3d.js';
import { COLOUR_CORE } from '@3dp/core/filaments';
import { createColours } from '@3dp/ui/colours';
import { recordOf, asListed, askedFor, offerFor, megabytes, listMayHelp, builtFontId } from '@3dp/core/fonts/choose';
import { curatedEntry, fontKey } from '@3dp/core/fonts/catalog';
import { PICKER_CORE } from '@3dp/core/fonts/picker-core';
import { createFontControl } from '@3dp/ui/font-control';
import { hit } from '@3dp/core/stats';
import { isLineBrowser, externalBrowserUrl, shareUrl, readShare } from './share.js';
import { wireLangSwitch } from '@3dp/ui/switch';   // ship 3 (R21): the switch's links carry the design
import { mountLangOffer } from '@3dp/ui/offer';

publishBarHeight(document.querySelector('.ui-bar'));   // --ui-bar-h: shell.css ends the desktop panel above the fixed bar
publishHeaderHeight(document.querySelector('header.ui-header')); // --ui-header-h: ship 3's taller header row; the desktop panel leaves its room too

const $ = (s) => document.querySelector(s);
const LIFT_REACH = 0.3;   // the lid rises this fraction of the cup's height at full lift (0.6 shrank the cup to fit the lifted lid)
const PART_ORDER = ['body', 'ring', 'logo', 'lid'];
const ART_TIMEOUT_MS = 10000;   // an `art` call with no reply for this long ends the worker: the engine cannot be interrupted
const START_TIMEOUT_MS = 60000; // a worker that has not said `ready` by then counts as failed to load (worker-client.js)
const BUILD_TIMEOUT_MS = 10000; // a build the worker has been ON for this long is given up: the worker is ended and a new one started (unify-ui ruling R22)
const SVG_MAX_BYTES = 1048576;  // the reader's limit in characters; a file has at least as many bytes, so a bigger one is refused unread
const THUMB = [96, 64];          // #thumb's CSS size in px (main.css)
const SKIPPED_KEYS = ['stroke', 'text', 'image', 'hidden', 'bad', 'background', 'clipped', 'stroked'];

// A worker that cannot work is permanent for this page: once true, renderIssues() always shows S.fatal. The client
// reports it for a `fatal` message, a Worker error event, or no `ready` within the start limit, and then answers
// every call { code: 'fatal' } — nothing is left waiting.
// The start limit is read each time a worker is started. The first one starts on the next line, before a test could
// set anything on the page's window.__CUP__ — so a test that shortens it defines window.__CUP__ = { startTimeoutMs }
// in an init script, before this script runs; the value is carried over into the page's own __CUP__ below.
const startLimit = () => { const v = window.__CUP__ && window.__CUP__.startTimeoutMs; return v > 0 ? v : START_TIMEOUT_MS; };
let fatal = false;
const worker = createWorkerClient({ url: window.__WORKER_URL__, recycleAfter: 300, startLimitMs: startLimit, onFatal: () => { fatal = true; render(); } });

// ---- state: doc (from a share link or the default), the uploaded art, the notice above the form, the last build ----
// `art` is the outline readSvg made of an uploaded file ({ points, rings, w, h, pieces }): numbers only. It lives in
// this variable and nowhere else — not in the doc, the link, storage or the DOM — so a reload forgets it (spec D10, R8).
const shareResult = readShare(location.hash);
let doc, part = 'set', art = null, notice = null;

// ship 3 (R21): the language switch and the English-page offer. An uploaded logo's file never
// travels in a link (forShare keeps the mode) — the note says so while the menu is open.
wireLangSwitch(window, { linkFor: (href) => shareUrl(href, doc), loses: () => doc.logo.src === 'svg' });
mountLangOffer(window, { linkFor: (href) => shareUrl(href, doc) });   // the offer's link carries the design too (B1)
if (shareResult === null) doc = sanitize(DEFAULT_DOC);
else if (shareResult.error) { notice = shareResult.error.code === 'newer-version' ? S.newerLink : S.badLink; doc = sanitize(DEFAULT_DOC); }
else { doc = shareResult.doc; if (doc.logo.src === 'svg') notice = S.linkNeedsLogo; }   // a link never carries the file (D11)
let builtDoc = null, builtArt = null, buildFailed = false, buildTimedOut = false, problems = [], meta = null, preview = null, previewFailed = false;
// The upload row: is a file being read, the last refusal, and the skipped counts on show (the kept art's, or a refused file's).
let reading = false, uploadError = null, artSkipped = null, shownSkipped = null;
let fontList = null;            // the list's entries (fonts/curated.json: 17 bundled, 12 downloaded), once it has loaded
let lastFont = null;            // the font of the last build that had its font: where a download that fails falls back to

// Window for the browser tests and the hub's ready signal (`ready`: window.__CUP__?.built === true). Everything here
// is read-only except the two limits a test may shorten: `artTimeoutMs` (read when a file is sent) to see the upload
// watchdog fire, `startTimeoutMs` (read when a worker is started; see `startLimit` above) to see a worker fail to start.
window.__CUP__ = {
  built: false, builds: 0, picks: 0, lastMs: null, problems: [], artTimeoutMs: ART_TIMEOUT_MS, startTimeoutMs: startLimit(),
  fontProgress: null,                                        // the last progress of a font download: { id, received, total } (read-only, for the tests)
  buildTimeoutMs: BUILD_TIMEOUT_MS,                          // a build's clock (read when a build is sent): a test may shorten it
  doc: () => doc, meta: () => meta, art: () => art,
  settled: () => builtDoc === doc && builtArt === art,      // the scene on screen was built from the current doc and art
  sceneCount: () => (preview ? preview.count() : 0),
  partRect: (name) => (preview ? preview.rectOf(name) : null),
  sceneRect: () => (preview ? preview.sceneRect() : null),
  logoRect: () => (preview ? preview.rectOf('logo') : null),
  canvasRect: () => { const r = $('#preview').getBoundingClientRect(); return { left: r.left, top: r.top, width: r.width, height: r.height }; },
};

// checkVisibility() is missing before Safari 17.4 / iOS 17.4; offsetParent is the fallback (as in tray's preview3d.js).
const shown = (el) => (typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.offsetParent !== null);
// Pixels at the canvas's bottom that the lid slider covers (a little air included): the preview frames the cup above them.
function reserve() {
  const c = $('#preview').getBoundingClientRect(), row = $('#lift-row');
  return { top: 0, bottom: shown(row) ? c.bottom - row.getBoundingClientRect().top + 8 : 0 };
}
hit('coffee-cup', 'open');
hit('coffee-cup', 'choice', `lang:${pageLanguage().id}`); // R25: the page's OWN address's language, never the browser's
try { preview = createPreview($('#preview'), { reserve }); } catch { previewFailed = true; }

// ---- static copy: every user-visible string comes from strings.js ----
const text = (sel, v) => { $(sel).textContent = v; };
text('#title', S.title);
text('#l-g-logo', S.gLogo); text('#l-g-size', S.gSize); text('#l-g-colour', S.gColour); text('#l-g-lid', S.gLid);
text('#l-text', S.text); text('#l-font', S.font); text('#l-d', S.d); text('#l-h', S.h);
text('#pick', S.pick); text('#l-mode', S.mode); text('#l-scale', S.scale); text('#l-pos', S.pos); text('#l-hide-thin', S.hideThin);
text('#about-hairline', S.hairline);
for (const [k, v] of Object.entries(S.sources)) text(`#src button[data-k="${k}"]`, v);
for (const [k, v] of Object.entries(S.styles)) text(`#style button[data-k="${k}"]`, v);
$('#style').setAttribute('aria-label', S.styleLabel);
for (const [k, v] of Object.entries(S.modes)) text(`#mode button[data-k="${k}"]`, v);
$('#thumb').setAttribute('aria-label', S.thumb);
text('#l-lid-on', S.lidOn); text('#l-ring-on', S.ringOn); text('#l-fit', S.fit); text('#l-part', S.partLabel);
text('#l-lift', S.lift);
for (const [k, v] of Object.entries(S.presets)) text(`#preset button[data-k="${k}"]`, v);
for (const [k, v] of Object.entries(S.fits)) text(`#fit button[data-k="${k}"]`, v);
for (const [k, v] of Object.entries(S.partNames)) text(`#part button[data-p="${k}"]`, v);
text('#dl3mf', S.dl3mf); text('#dlstl', S.dlstl); text('#share', S.share);
$('#lift').setAttribute('aria-label', S.lift);
if (previewFailed) { text('#busy', S.noGl); $('#busy').classList.remove('hidden'); }

// ---- the colours (unify-ui ship 4b: @3dp/ui/colours, D3 — name-tag's control): the four parts' rows, each a swatch with the
// colour's name under it, every filament and brand colour plus the visitor's own in one tap; the colour sets (this tool's
// own, D3) as strips. An own colour is stored and shown; for the slots it counts as its nearest filament (parts.js, R99). ----
const PARTS = ['body', 'lid', 'ring', 'logo'];
const colours = createColours({ root: $('#colours'), S, core: COLOUR_CORE, looksLabel: S.looks,
  onChange: (key, hex) => change({ ...doc, colors: { ...doc.colors, [key]: hex } }),
  onLook: (key) => { if (Object.hasOwn(LOOKS, key)) change({ ...doc, colors: { ...LOOKS[key] } }); } });
const LOOK_LIST = Object.entries(LOOKS).map(([key, c]) => ({ key, name: S.lookNames[key], colours: { ...c } }));

// ---- render: doc → every control, readout, legend, issues, buttons ----
const round1 = (n) => Math.round(n * 10) / 10;
function renderReadout() {
  const lines = ['#r-outer', '#r-foot', '#r-cap'];
  if (!meta) { for (const l of lines) text(l, ''); return; }
  text('#r-outer', S.outer(round1(meta.outer.d), round1(meta.outer.h)));
  text('#r-foot', S.footprint(round1(meta.footprint.w), round1(meta.footprint.d)));
  text('#r-cap', S.capacity(meta.capacityMl < 10 ? round1(meta.capacityMl) : Math.round(meta.capacityMl)));
}
function renderLegend() {
  const ul = $('#legend');
  ul.replaceChildren();
  for (const e of meta ? meta.extruders : []) {
    const li = document.createElement('li'), sw = document.createElement('span');
    sw.className = 'sw'; sw.style.background = e.hex;
    li.append(sw, `${S.slot(e.n)} ${e.parts.map((p) => S.parts[p]).join(' · ')}`);
    ul.append(li);
  }
}
const rank = { error: 0, warn: 1, info: 2 };
function problemText(p) {
  const m = Object.hasOwn(S.problem, p.code) ? S.problem[p.code] : p.code;
  return typeof m === 'function' ? m(p) : m;
}
function renderIssues() {
  const ul = $('#issues');
  ul.replaceChildren();
  const add = (msg, level) => { const li = document.createElement('li'); li.textContent = msg; if (Object.hasOwn(rank, level)) li.className = level; ul.append(li); return li; };
  // Beside a report of characters the font lacks: ONE family that has them, in one tap, with its size (ruling R14) — by
  // the script of the missing characters, and only while the font in use is a bundled one (one offer, no chain: after
  // the download the line names what is still missing, and the list stays for a choice by hand). Nothing is asked of
  // any host before the tap.
  const offer = (li, chars) => {
    const entry = fontList && offerFor(chars, fontList, doc.logo.font, pageLanguage().id);   // ship 3: a ja / zh page offers its own family first
    if (!entry && listMayHelp(chars, doc.logo.font)) { const n = document.createElement('span'); n.className = 'ui-offer-note'; n.textContent = S.pickFromList; li.append(n); }   // R83
    if (!entry) return;
    const b = document.createElement('button'); b.type = 'button'; b.className = 'ui-offer'; b.dataset.font = entry.id;
    b.textContent = S.useFont(entry.family, megabytes(entry.bytes));
    b.addEventListener('click', () => setLogo({ font: recordOf(entry) }));
    li.append(b);
  };
  if (fatal) { add(S.fatal, 'error'); return; }
  if (buildTimedOut) add(S.buildTimeout(doc.logo.src), 'error');
  if (buildFailed) add(S.buildFail, 'error');
  for (const p of [...problems].sort((a, b) => rank[a.level] - rank[b.level])) { const li = add(problemText(p), p.level); if (p.code === 'missing-chars') offer(li, p.chars); }
}
// The upload row: the button, the thumbnail of the art in use, what the reader skipped, and one line of status.
function renderUpload() {
  $('#pick').disabled = reading;
  $('#thumb').classList.toggle('hidden', !art);
  const ul = $('#skipped');
  ul.replaceChildren();
  for (const k of SKIPPED_KEYS) {
    const n = shownSkipped ? shownSkipped[k] : 0;
    if (!Number.isInteger(n) || n <= 0) continue;
    const li = document.createElement('li'); li.dataset.k = k; li.textContent = S.skipped[k](n); ul.append(li);
  }
  const note = $('#upload-note'), refused = !reading && uploadError !== null;
  note.classList.toggle('error', refused);
  note.textContent = reading ? S.reading : refused ? (art ? `${uploadError} ${S.keptOld}` : uploadError) : art ? S.artKept : S.needFile;
}
// The art's rings, drawn from the numbers (never from the file): art is y up and centred on its box, a canvas is y down.
function drawThumb() {
  const c = $('#thumb'), dpr = Math.min(window.devicePixelRatio || 1, 2);
  c.width = THUMB[0] * dpr; c.height = THUMB[1] * dpr;             // the CSS box (main.css) times the screen's density
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, c.width, c.height);
  if (!art || !(art.w > 0 && art.h > 0)) return;
  const pad = 6 * dpr, s = Math.min((c.width - 2 * pad) / art.w, (c.height - 2 * pad) / art.h), cx = c.width / 2, cy = c.height / 2;
  ctx.beginPath();
  let k = 0;
  for (const n of art.rings) {
    for (let j = 0; j < n; j++, k += 2) {
      const x = cx + s * art.points[k], y = cy - s * art.points[k + 1];
      if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
  }
  ctx.fillStyle = getComputedStyle(c).color;      // the page's text colour
  ctx.fill('evenodd');
}
// Bring a message into the part of the viewport the fixed bar does not cover (scroll-margin in main.css keeps the bar's height free).
function reveal(el) {
  if (!shown(el)) return;
  const r = el.getBoundingClientRect();
  if (r.top < 0 || r.bottom > $('.bar').getBoundingClientRect().top) el.scrollIntoView({ block: 'nearest' });
}
const hasError = () => problems.some((p) => p.level === 'error');
// a pill's lit state is said twice: by colour (.on) and to assistive tech (aria-pressed)
const pill = (b, on) => { b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); };
function render() {
  const key = sizeKey(doc), { src } = doc.logo;
  $('#link-notice').textContent = notice || ''; $('#link-notice').classList.toggle('hidden', !notice);
  for (const b of $('#src').children) pill(b, b.dataset.k === src);
  for (const b of $('#mode').children) pill(b, b.dataset.k === doc.logo.mode);
  for (const id of ['#text-row', '#font-row']) $(id).classList.toggle('hidden', src !== 'text');
  for (const id of ['#upload-row', '#hide-thin-row']) $(id).classList.toggle('hidden', src !== 'svg');
  $('#logo-look').classList.toggle('hidden', src === 'none');         // nothing to style without a logo
  fonts.setFont();
  $('#scale').value = String(doc.logo.scale); text('#o-scale', S.percent(doc.logo.scale));
  $('#pos').value = String(doc.logo.pos); text('#o-pos', S.signedPercent(doc.logo.pos));
  $('#hide-thin').checked = doc.logo.hideThin;
  for (const b of $('#style').children) pill(b, b.dataset.k === doc.style);
  text('#mode-note', S.modeNote(doc.style, doc.logo.mode));
  renderUpload();
  for (const b of $('#preset').children) pill(b, b.dataset.k === key);
  for (const b of $('#fit').children) pill(b, b.dataset.k === doc.fit);
  if (!doc.lid && part === 'lid') part = 'set';
  for (const b of $('#part').children) pill(b, b.dataset.p === part);
  $('#part button[data-p="lid"]').disabled = !doc.lid;
  colours.set(PARTS.map((p) => ({ key: p, label: S.colour[p], value: doc.colors[p] })), LOOK_LIST);
  for (const id of ['d', 'h']) if (document.activeElement !== $('#' + id)) $('#' + id).value = fmt(doc.size[id]);
  if (document.activeElement !== $('#text')) $('#text').value = doc.logo.text;
  $('#lid-on').checked = doc.lid; $('#ring-on').checked = doc.ring;
  text('#fit-note', S.fitNote(doc.style, doc.fit, doc.size.d));
  $('#lift-row').classList.toggle('hidden', !doc.lid);
  renderReadout(); renderLegend(); renderIssues();
  const ok = !fatal && builtDoc === doc && builtArt === art && !buildFailed && !hasError();
  for (const id of ['#dl3mf', '#dlstl', '#share']) $(id).disabled = id === '#share' ? false : !ok;
}

// ---- change: every control goes through here. A notice about the link goes away with the first edit, unless the
// edit is the page's own correction (`keepNotice`). ----
function change(next, { keepNotice = false } = {}) {
  doc = sanitize(next);
  if (!keepNotice) notice = null;
  render(); scheduleBuild();
  window.history.replaceState(null, '', shareUrl(location.href, doc));
}

// ---- worker build loop: 120 ms debounce, latest wins (a superseded build is answered `stale`) ----
let buildTimer = null;
function scheduleBuild() { clearTimeout(buildTimer); buildTimer = setTimeout(build, 120); }
// One engine fault means the wasm engine in the worker is dead: replace the worker and ask once more. For builds
// and exports only — an `art` call that faults is never sent again (readFile, below). An export that was waiting
// when the worker was swapped for another request's sake is asked again too.
// A BUILD has a clock (unify-ui ruling R22): 10 s on the worker's own work, counted from its `started` — never while it
// waits behind another request or for a font file. callOnce, because the clock belongs to the worker the build went to.
const ask = (msg, onProgress) => (msg.type === 'build' ? worker.callOnce(msg, { limitMs: window.__CUP__.buildTimeoutMs, from: 'started', onProgress }) : worker.call(msg));
async function callWorker(msg, onProgress) {
  let r = await ask(msg, onProgress);
  if (r.error && r.error.code === 'engine') { worker.replace(); r = await ask(msg, onProgress); }
  else if (r.error && r.error.code === 'replaced' && msg.type === 'export') r = await worker.call(msg);
  return r;
}
function showScene(reply, sent) {
  if (!preview) return;
  const by = Object.fromEntries(reply.parts.map((p) => [p.name, p]));
  preview.setScene(PART_ORDER.filter((n) => by[n]).map((n) => ({
    name: n, mesh: by[n].mesh, color: by[n].color,
    ...(n === 'lid' ? { pos: [0, 0, reply.meta.lidWornZ], explode: [0, 0, LIFT_REACH * sent.size.h] } : {}),
  })));
  preview.setLift(Number($('#lift').value));
}
let shownCodes = [];
async function build() {
  text('#busy', S.updating); $('#busy').classList.remove('hidden');
  const sent = doc, sentArt = art;
  // A downloaded family's file on its way: the line says which and how far (the worker posts progress per piece).
  const family = (fontList && curatedEntry(sent.logo.font, fontList) || {}).family || sent.logo.font.id;   // a Google family's id IS its name
  let progressSeen = false;
  const onProgress = (p) => {
    window.__CUP__.fontProgress = { id: sent.logo.font.id, received: p.received, total: p.total };
    if (doc === sent) text('#busy', S.loadingFont(family, p.received / 1048576, p.total / 1048576));
    // the line is in the preview and the font list is below it: it is brought into view once, when it first shows (unify-ui ruling R65)
    if (doc === sent && !progressSeen) { progressSeen = true; reveal($('#busy')); }
  };
  const reply = await callWorker({ type: 'build', doc: sent, art: sentArt }, onProgress);   // no transfer list: the page keeps its art
  const code = reply.error && reply.error.code;
  if (code === 'stale') return;                                  // a newer build is already on its way
  if (code === 'replaced') { scheduleBuild(); return; }          // the worker was swapped under this request
  if (doc === sent) $('#busy').classList.add('hidden');   // an older build's reply leaves the line to the build on its way (R106): its reply takes it down
  // The clock fired: the engine cannot be interrupted, so the worker is ended and a new one started, and the page says
  // so. Nothing is built from this document (the downloads stay off); the next edit goes to the new worker.
  if (code === 'timeout') {
    worker.replace(); buildTimedOut = true; render();
    if (!shownCodes.includes('build-timeout')) reveal($('#issues'));
    shownCodes = ['build-timeout'];
    return;
  }
  buildTimedOut = false;
  // A downloaded family that could not be had (unify-ui acceptance 11): the page stays usable. The design goes back to the
  // font it last built in (Kanit when there is none) and a notice names the family — unless the visitor has already
  // picked another font, which then simply builds.
  if (code === 'font-load' && sent.logo.font.src === 'google') {
    notice = S.fontFailed(family);
    fonts.markFailed(fontKey(sent.logo.font));                     // its row in the sheet says so at the next open (as name-tag's)
    if (doc.logo.font === sent.logo.font) change({ ...doc, logo: { ...doc.logo, font: { ...(lastFont || DEFAULT_DOC.logo.font) } } }, { keepNotice: true });
    else render();
    reveal($('#link-notice'));
    return;
  }
  // A family neither list holds (ruling R100): the worker built the design in Kanit and says so — the notice the page
  // showed for it before, and the document to Kanit (no undo step) — unless the visitor has picked another font since.
  if (reply.ok && reply.fellBack && doc.logo.font === sent.logo.font) { notOffered(sent.logo.font); return; }
  builtDoc = sent; builtArt = sentArt; buildFailed = reply.ok === false;
  if (!buildFailed) lastFont = sent.logo.font;
  const firstBuild = !window.__CUP__.built;
  if (buildFailed) problems = [];
  else {
    problems = reply.problems || []; meta = reply.meta;
    window.__CUP__.problems = problems; window.__CUP__.lastMs = reply.ms;
    showScene(reply, sent);
    window.__CUP__.built = true; window.__CUP__.builds++;
  }
  render();
  // A problem that was not on show a moment ago is brought into view (spec acceptance 13); one that stays does not pull the page.
  const codes = [...problems.map((p) => p.code), ...(buildFailed ? ['build-failed'] : [])];
  if (codes.some((c) => !shownCodes.includes(c))) reveal($('#issues'));
  shownCodes = codes;
  // a link's notice was brought into view when the font list arrived, before this first build put its lines above it: once more now, so that it stops above the bar (unify-ui ruling R65)
  if (firstBuild && notice) reveal($('#link-notice'));
  if (preview) preview.refit();                                  // the legend's height changed: frame the cup in what is left
  worker.countGeneration(); worker.recycleIfDue();
}

// ---- size ----
$('#preset').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (!b) return;
  const [d, h] = PRESETS[b.dataset.k];
  for (const id of ['d', 'h']) $('#' + id + '-hint').classList.add('hidden');
  change({ ...doc, size: { d, h } });
});
const sizeLimits = (id) => (id === 'd' ? LIMITS.d : [Math.ceil(LIMITS.hRatio[0] * doc.size.d * 100) / 100, Math.floor(LIMITS.hRatio[1] * doc.size.d * 100) / 100]);
const sizeWith = (id, n) => (id === 'd' ? { d: n, h: (doc.size.h * n) / doc.size.d } : { d: doc.size.d, h: n });   // a new width keeps the cup's proportions
for (const id of ['d', 'h']) {
  const el = $('#' + id), hintEl = $('#' + id + '-hint');
  const parse = () => { const raw = el.value.trim().replace(',', '.'); return raw === '' ? NaN : Number(raw); };
  el.addEventListener('input', () => {
    const n = parse(), [lo, hi] = sizeLimits(id);
    if (Number.isFinite(n) && n >= lo && n <= hi) { hintEl.classList.add('hidden'); change({ ...doc, size: sizeWith(id, n) }); }
    else { text('#' + id + '-hint', S.numHint(lo, hi)); hintEl.classList.remove('hidden'); }
  });
  el.addEventListener('blur', () => {
    const n = parse(), [lo, hi] = sizeLimits(id);
    if (!Number.isFinite(n)) hintEl.classList.add('hidden');       // empty or not a number: the previous value stays
    else if (n < lo || n > hi) {
      change({ ...doc, size: sizeWith(id, Math.min(hi, Math.max(lo, n))) });
      text('#' + id + '-hint', S.adjusted(fmt(doc.size[id]), lo, hi)); hintEl.classList.remove('hidden');
    }
    el.value = fmt(doc.size[id]);
  });
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); el.blur(); } });
}

// ---- logo: source, text and font, look ----
const setLogo = (patch) => change({ ...doc, logo: { ...doc.logo, ...patch } });
const segPick = (sel, allowed, key) => $(sel).addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (b && allowed.includes(b.dataset.k)) setLogo({ [key]: b.dataset.k });
});
segPick('#src', SOURCES, 'src'); segPick('#mode', MODES, 'mode');
$('#text').addEventListener('input', () => setLogo({ text: $('#text').value }));
$('#text').addEventListener('blur', () => { $('#text').value = doc.logo.text; });
$('#scale').addEventListener('input', () => setLogo({ scale: Number($('#scale').value) }));
$('#pos').addEventListener('input', () => setLogo({ pos: Number($('#pos').value) }));
$('#hide-thin').addEventListener('change', (e) => setLogo({ hideThin: e.target.checked }));

// The font control (unify-ui ship 4a — @3dp/ui/font-control): the button, the weight select, the line for a family not yet
// print-tested (I4), and the sheet behind the button — the 29 recommended families, then the whole Google catalog (R91).
// fonts/curated.json is the file the worker reads too. A record that IS an entry of the list is held as the entry's own
// (R95, asListed). A design whose font this page cannot build in opens in Kanit: silently for an unknown bundled id, as
// always; with a notice that names what was asked for — an uploaded font at once, a family neither list holds when the
// worker says it built in Kanit instead (its reply's `fellBack`, ruling R100). Only the fetch is allowed to fail quietly (the
// button then stays off, Kanit the font the worker always has): an error in the code below it is a bug and must be seen.
const fonts = createFontControl({ root: $('#font-pick'), S, core: PICKER_CORE, getFont: () => doc.logo.font, getText: () => doc.logo.text,
  onPick: (f) => setLogo({ font: asListed(recordOf(f), fontList) }),
  onWeight: (font) => setLogo({ font }) });
function notOffered(asked) {
  change({ ...doc, logo: { ...doc.logo, font: { ...DEFAULT_DOC.logo.font } } }, { keepNotice: true });
  if (asked.src !== 'bundled') { notice = [notice, S.fontNotOffered(askedFor(asked))].filter(Boolean).join(' '); render(); reveal($('#link-notice')); }   // an unknown bundled id: silently, as always
}
fetch(asset('fonts/curated.json')).then((r) => r.json()).catch(() => null).then((list) => {
  if (!Array.isArray(list)) return;
  fontList = list;
  fonts.setList(list);
  const asked = doc.logo.font;
  if (asked.src === 'google' || curatedEntry(asked, fontList)) {
    const held = asListed(asked, fontList);
    if (fontKey(held) !== fontKey(asked)) setLogo({ font: held }); else render();   // a list entry, named by another record
    return;
  }
  change({ ...doc, logo: { ...doc.logo, font: { ...DEFAULT_DOC.logo.font } } }, { keepNotice: true });
  if (asked.src !== 'bundled') { notice = [notice, S.fontNotOffered(askedFor(asked))].filter(Boolean).join(' '); render(); reveal($('#link-notice')); }
});

// ---- uploaded logo (spec D10). The file's text is a local of readFile() and is handed to the worker, which hands it to
// readSvg: it is never put in the DOM, a URL, storage, a log or the doc. The file's name is read once, for its
// extension, and the input lets go of the file as soon as it is picked. ----
// The limit is for the engine's work on the file. callOnce() waits for the worker that is current now, posts the file
// to that worker and starts the clock at that moment: on a slow first load the wait for wasm and fonts is not the
// file's doing, and a file is never posted after its clock has fired. (A build already running in the worker still
// counts — the worker has no way to say "starting your file now".) Replies of its own: `replaced` — that worker was
// swapped, before or after the file was posted; `timeout`; `fatal` — the worker cannot work at all.
const askArt = (svg) => worker.callOnce({ type: 'art', svg }, { limitMs: window.__CUP__.artTimeoutMs });
// → { art, skipped } or { error: message, skipped? }.
async function readFile(file) {
  if (!/\.svg$/i.test(file.name)) return { error: S.notSvg };
  if (file.size > SVG_MAX_BYTES) return { error: S.svgTooBig };
  let svg;
  try { svg = await file.text(); } catch { return { error: S.readFail }; }
  let r = await askArt(svg);
  if (r.error && r.error.code === 'replaced') r = await askArt(svg);   // a build's engine fault swapped the worker under this call: start over, once — the new worker, a new clock
  const code = r.ok ? null : r.error && r.error.code;
  if (code === 'engine' || code === 'timeout') {
    // This file broke the engine, or kept it past the limit. A fresh worker; the file is not sent again; the page builds as before.
    worker.replace(); scheduleBuild();
    return { error: S.svgTooBig };
  }
  // The reader's three verdicts name the file's fault. Anything else (`internal`, a second `replaced`) says nothing
  // about the file, which may be a good one: the message asks to try again and does not call it a bad SVG.
  if (code === 'fatal') return { error: S.fatal };                // the worker cannot work: no file can be read on this page
  const verdict = { 'svg-too-big': 'svgTooBig', 'svg-empty': 'svgEmpty', 'svg-bad': 'svgBad' };
  if (!r.ok) return { error: S[Object.hasOwn(verdict, code) ? verdict[code] : 'readFail'], skipped: r.skipped };
  return { art: r.art, skipped: r.skipped };
}
async function pickFile(file) {
  reading = true; render();
  let r;
  try { r = await readFile(file); } catch { r = { error: S.readFail }; }      // whatever happens, the button comes back
  reading = false;
  if (r.art) { art = r.art; artSkipped = shownSkipped = r.skipped || null; uploadError = null; scheduleBuild(); }
  else { uploadError = r.error; shownSkipped = r.skipped || artSkipped; }      // a refused file changes nothing but the message: the earlier art stays
  window.__CUP__.picks++;
  render(); reveal($('#upload-row'));
  if (r.art) drawThumb();
}
$('#pick').addEventListener('click', () => $('#file').click());
$('#file').addEventListener('change', () => {
  const file = $('#file').files[0];
  $('#file').value = '';             // the input forgets the file (and its name); the same file can be picked again
  if (file && !reading) pickFile(file);
});

// ---- lid, ring, download choice ----
$('#lid-on').addEventListener('change', (e) => change({ ...doc, lid: e.target.checked }));
$('#ring-on').addEventListener('change', (e) => change({ ...doc, ring: e.target.checked }));
$('#fit').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (b && Object.hasOwn(FITS, b.dataset.k)) change({ ...doc, fit: b.dataset.k });
});
$('#style').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (b && STYLE_KEYS.includes(b.dataset.k)) change({ ...doc, style: b.dataset.k });
});
$('#part').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-p]');
  if (b && !b.disabled) { part = b.dataset.p; render(); }
});
$('#lift').addEventListener('input', (e) => { if (preview) preview.setLift(Number(e.target.value)); });

// ---- downloads (anchor attached before click(): a detached <a download> is ignored by Safari/iOS) ----
async function download(format) {
  const sent = doc, sentPart = part;                              // the choices below describe the file that is made, not a later edit
  const r = await callWorker({ type: 'export', doc: sent, art, format, part: sentPart });   // `art` is read now, in the same task as `doc`
  worker.countGeneration(); worker.recycleIfDue();
  if (!r.ok) { const li = document.createElement('li'); li.textContent = S.exportFail; $('#issues').append(li); return; }
  const href = URL.createObjectURL(new Blob([r.bytes], { type: r.mime }));
  const a = document.createElement('a'); a.href = href; a.download = r.filename;
  document.body.appendChild(a); a.click(); a.remove();
  // Choices are sent once per download: they measure what actually gets printed. Never the text, never the file.
  hit('coffee-cup', 'download', format);
  hit('coffee-cup', 'choice', 'size:' + sizeKey(sent));
  hit('coffee-cup', 'choice', 'logo:' + sent.logo.src);
  hit('coffee-cup', 'choice', 'mode:' + sent.logo.mode);
  hit('coffee-cup', 'choice', 'fit:' + sent.fit);
  hit('coffee-cup', 'choice', 'style:' + sent.style);
  // the id — a slug, or a family name — of the font the worker BUILT the file in: a name out of a link that no list holds
  // is never sent (the worker built it in Kanit and said so, `fellBack`); the registry's pattern takes every catalog family
  if (sent.logo.src === 'text') hit('coffee-cup', 'choice', 'font:' + builtFontId(r, sent.logo.font, DEFAULT_DOC.logo.font));
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}
function wireDownload(btn, format) {
  btn.addEventListener('click', () => {
    if (isLineBrowser(navigator.userAgent)) {
      text('#line-hint', S.lineHint);
      $('#line-hint').classList.remove('hidden');
      location.href = externalBrowserUrl(location.href);
      return;
    }
    download(format);
  });
}
wireDownload($('#dl3mf'), '3mf');
wireDownload($('#dlstl'), 'stl');

// ---- share ----
$('#share').addEventListener('click', async () => {
  const sent = doc, u = shareUrl(location.href, sent);            // forShare(): with an uploaded logo the link carries the mode, never the file or a text
  try { await navigator.clipboard.writeText(u); } catch { prompt('', u); }
  hit('coffee-cup', 'share');
  if (sent.logo.src === 'svg') { notice = S.shareNoLogo; render(); reveal($('#link-notice')); }
  text('#share', S.copied);
  setTimeout(() => { text('#share', S.share); }, 1500);
});

// ---- start ----
render();
build();
